import React from 'react';
import { i18n } from 'config/locales';
import { FormError } from '@redux/modules/form/types';
import { CheckboxModel } from 'forms/shared/types';
import ValidationElement from '../ValidationElement';
import { ariaLabel } from '../Generic';
import { ValidationElementProps } from '../ValidationElement/ValidationElement';

interface Props extends ValidationElementProps, Partial<HTMLInputElement>, Partial<FormError>, Partial<CheckboxModel> {
}

export default class Checkbox extends ValidationElement<Props> {
  constructor(props) {
    super(props);

    this.state = {
      uid: `${this.props.name}-${super.guid()}`,
      checked: props.checked,
      focus: props.focus || false,
      error: props.error || false,
      valid: props.valid || false,
      watchChecked: false,
    };

    this.handleError = this.handleError.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);

    this.checkbox = React.createRef();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.checked !== this.props.checked) {
      this.setState({
        checked: nextProps.checked,
      });
    }
  }

  /**
   * Update the value of the checkbox
   */
  update() {
    const checked = (!this.state.checked); // eslint-disable-line react/no-access-state-in-setstate
    const value = this.props.value;
    if (
      checked === true
      && this.props.includeNAConfirmation
      && window?.confirm(i18n.t('application.alert.notApplicable')) === false
    ) {
      this.setState({ checked: false });
    } else {
      this.setState({ checked }, () => {
        if (this.props.onUpdate) {
          this.props.onUpdate({
            name: this.props.name,
            value,
            checked,
          });
        }

        this.handleValidation();
      });
    }
  }

  /**
   * Handle the change event.
   */
  handleChange(event) {
    this.update();
  }

  /**
   * Handle the key press event.
   */
  handleKeyPress(event) {
    const allowedKeys = [' ', 'Enter'];
    if (allowedKeys.includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      this.update();
    }
  }

  /**
   * Handle the focus event.
   */
  handleFocus(event) {
    this.setState({ focus: true }, () => {
      super.handleFocus(event);
    });
  }

  /**
   * Handle the blur event.
   */
  handleBlur(event) {
    this.setState({ focus: false }, () => {
      super.handleBlur(event);
    });
  }

  handleError(value, arr = []) {
    const errors = this.props.onError(
      value,
      this.constructor.errors.map((err) => ({
        code: err.code,
        valid: value ? err.func(value, this.props) : null,
        uid: this.state.uid,
      }))
    ) || [];

    this.setState({
      error: errors.some((x) => !x.valid),
      valid: errors.every((x) => x.valid),
    });
  }

  /**
   * Execute validation checks on the value.
   */
  handleValidation(event) {
    this.handleError(this.state.checked);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.forceRefresh !== prevProps.forceRefresh) {
      this.setState({ // eslint-disable-line react/no-did-update-set-state
        checked: this.props.checked,
        watchChecked: true,
      });
    }
    if (this.state.watchChecked && this.props.checked !== prevProps.checked) {
      this.setState({ // eslint-disable-line react/no-did-update-set-state

        checked: this.props.checked,
        watchChecked: false,
      });
    }
  }

  /**
   * Style classes applied to the wrapper.
   */
  divClass() {
    let klass = `${this.props.className || ''} block`;

    if (this.props.children) {
      klass += ' extended';
    }

    if (this.props.disabled) {
      klass += ' disabled';
    }

    if (this.state.error) {
      klass += ' usa-input-error';
    }

    return klass.trim();
  }

  /**
   * Style classes applied to the label element.
   */
  labelClass() {
    let klass = 'checkbox';

    if (this.state.error) {
      klass += ' usa-input-error-label';
    }

    if (this.state.checked) {
      klass += ' checked';
    }

    if (this.props.toggle === 'false') {
      klass += ' no-toggle';
    }

    return klass.trim();
  }

  /**
   * Style classes applied to the input element.
   */
  inputClass() {
    let klass = '';

    if (this.state.valid) {
      klass += ' usa-input-success';
    }

    return klass.trim();
  }

  render() {
    let speech = '';

    if (this.props.ariaLabel) {
      speech = this.props.ariaLabel;
    } else if (this.props.label && this.checkbox?.current?.ariaLabel) {
      speech = `${this.props.ariaPrepend} ${this.props.label} for ${ariaLabel(this.checkbox.current)}`;
    } else {
      speech = `${this.props.ariaPrepend} ${this.props.label}`;
    }

    if (this.props.toggle === 'false') {
      return (
        <div className={this.divClass()}>
          <input
            className={this.inputClass()}
            id={this.state.uid}
            name={this.props.name}
            type="checkbox"
            ref={this.checkbox}
            disabled={this.props.disabled}
            readOnly={this.props.readonly}
            value={this.props.value}
            onChange={this.handleChange} // eslint-disable-line @typescript-eslint/unbound-method
            onKeyDown={this.handleKeyPress} // eslint-disable-line @typescript-eslint/unbound-method
            onFocus={this.handleFocus} // eslint-disable-line @typescript-eslint/unbound-method
            onBlur={this.handleBlur} // eslint-disable-line @typescript-eslint/unbound-method
            checked={this.state.checked}
            aria-label={speech}
          />
          <label className={this.labelClass()} htmlFor={this.state.uid}>
            {this.props.children}
            <span>{this.props.label}</span>
          </label>
        </div>
      );
    }

    return (
      <div className={this.divClass()}>
        <input
          className={this.inputClass()}
          id={this.state.uid}
          name={this.props.name}
          type="checkbox"
          ref={this.checkbox}
          disabled={this.props.disabled}
          readOnly={this.props.readonly}
          value={this.props.value}
          onChange={this.handleChange} // eslint-disable-line @typescript-eslint/unbound-method
          onKeyDown={this.handleKeyPress} // eslint-disable-line @typescript-eslint/unbound-method
          onFocus={this.handleFocus} // eslint-disable-line @typescript-eslint/unbound-method
          onBlur={this.handleBlur} // eslint-disable-line @typescript-eslint/unbound-method
          checked={this.state.checked}
          aria-label={speech}
        />
        <label className={this.labelClass()} htmlFor={this.state.uid}>
          {this.props.children}
          <span>{this.props.label}</span>
        </label>
      </div>
    );
  }
}

Checkbox.defaultProps = {
  name: 'checkbox_input',
  ariaPrepend: '',
  checked: false,
  focus: false,
  error: false,
  valid: false,
  forceRefresh: 0,
  onError: (value, arr) => arr,
  includeNAConfirmation: false,
};

Checkbox.errors = [];

Checkbox.select = (selectedObj, listObj) => {
  const selected = selectedObj.value;
  const list = [...((listObj || {}).values || [])];

  if (list.includes(selected)) {
    list.splice(list.indexOf(selected), 1);
  } else {
    list.push(selected);
  }

  return list;
};
